import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import { getSurahsFromFirestore, syncFromApi, backupQuranData, publishToApp } from '../../services/quranService';
import Pagination from '../../components/Pagination';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Typography, Box, CircularProgress, Snackbar, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Chip
} from '@material-ui/core';
import {  makeStyles , useTheme } from "@material-ui/core/styles";
import EditIcon from '@material-ui/icons/Edit';
import SyncIcon from '@material-ui/icons/Sync';
import BackupIcon from '@material-ui/icons/Backup';
import PublishIcon from '@material-ui/icons/Publish';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  tableContainer: {
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    background: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingText: {
    marginTop: 16,
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  actionBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  },
  orderBadge: {
    background: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    padding: '4px 8px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  publishBtn: {
    backgroundColor: '#10b981',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#059669',
    }
  },
  countChip: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.85rem',
    height: 36,
    padding: '0 8px',
    borderRadius: 10,
  },
}));

export default function QuranListPage() {
  const classes = useStyles();
  
  const theme = useTheme();
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  
  // Sync States
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');

  const pageSize = 20;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getSurahsFromFirestore();
      setItems(result);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load Quran data' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSyncClick = () => {
    if (items.length > 0) {
      setSyncDialogOpen(true);
    } else {
      startSync(); // direct sync if empty
    }
  };

  const startSync = async () => {
    setSyncDialogOpen(false);
    setSyncing(true);
    try {
      await syncFromApi((progressMsg) => {
        setSyncProgress(progressMsg);
      });
      setSnackbar({ open: true, message: 'Sync completed successfully!' });
      loadData();
    } catch (e) {
      setSnackbar({ open: true, message: 'Error during sync' });
    }
    setSyncing(false);
  };

  const startBackup = async () => {
    setSyncing(true);
    setSyncDialogOpen(false);
    try {
      await backupQuranData((progressMsg) => {
        setSyncProgress(progressMsg);
      });
      setSnackbar({ open: true, message: 'Backup completed successfully!' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Error during backup' });
    }
    setSyncing(false);
  };

  const handlePublish = async () => {
    try {
      await publishToApp();
      setSnackbar({ open: true, message: 'Published to mobile app successfully!' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to publish' });
    }
  };

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const paginatedItems = items.slice((page - 1) * pageSize, page * pageSize);

  return (
    <AdminLayout title="Quran Repository">
      <div className={classes.toolbar}>
        <Box display="flex" style={{ gap: 16 }} alignItems="center" flexWrap="wrap">
          <Chip label={`Total Surahs: ${items.length}`} className={classes.countChip} />
          <Button
            variant="contained"
            className={classes.publishBtn}
            startIcon={<PublishIcon />}
            onClick={handlePublish}
            disableElevation
          >
            Publish to App
          </Button>
        </Box>
        <Box display="flex" style={{ gap: 16 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SyncIcon />}
            onClick={handleSyncClick}
            disabled={syncing}
            disableElevation
          >
            {syncing ? 'Processing...' : 'Sync from API'}
          </Button>
        </Box>
      </div>

      {syncing && (
        <Paper style={{ padding: 20, marginBottom: 20, textAlign: 'center', background: 'rgba(56,189,248,0.1)', color: '#38bdf8' }}>
          <CircularProgress size={24} style={{ color: '#38bdf8', marginBottom: 10 }} />
          <Typography variant="body1">{syncProgress}</Typography>
        </Paper>
      )}

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} thickness={4} />
          <Typography className={classes.loadingText}>Loading records...</Typography>
        </div>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="80">No.</TableCell>
                <TableCell>Surah Name</TableCell>
                <TableCell>English Name</TableCell>
                <TableCell>Revelation</TableCell>
                <TableCell>Ayahs</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedItems.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <span className={classes.orderBadge}>{item.number}</span>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" color="textPrimary" style={{ fontWeight: 500, fontFamily: 'sans-serif' }}>
                      {item.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
                      {item.englishName} ({item.englishNameTranslation})
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {item.revelationType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {item.numberOfAyahs}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View & Edit Details">
                      <IconButton className={classes.actionBtn} onClick={() => history.push(`/quran/${item.number}`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && !syncing && (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: "48px 0" }} color="textSecondary">
                    No Quran data found. Please click "Sync from API" to fetch data.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && items.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={items.length}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      )}

      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)}>
        <DialogTitle>Confirm Sync</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Quran data already exists in the database. Syncing from the API will overwrite current data. Do you want to backup the data before syncing?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)} color="default">
            Cancel
          </Button>
          <Button onClick={startBackup} color="primary" startIcon={<BackupIcon />}>
            Backup Current Data
          </Button>
          <Button onClick={startSync} color="secondary" startIcon={<SyncIcon />}>
            Sync Now (Overwrite)
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </AdminLayout>
  );
}
